import { reportDate } from '../../reportClock';
export const AGENT_COLORS = [
  "#3b82f6",
  "#22c55e",
  "#a855f7",
  "#f97316",
  "#eab308",
  "#06b6d4",
  "#94a3b8",
];

export const toLocalDateValue = (value = new Date()) => {
  const date = value instanceof Date ? value : new Date(value);
  return reportDate(date);
};

export const getRelativeDateValue = (offsetDays) => {
  const date = new Date(`${reportDate()}T12:00:00-05:00`);
  date.setUTCDate(date.getUTCDate() + offsetDays);
  return toLocalDateValue(date);
};

export const formatReplayTime = (minute) => {
  const boundedMinute = Math.max(0, Math.min(1439, Math.round(Number(minute) || 0)));
  return `${String(Math.floor(boundedMinute / 60)).padStart(2, "0")}:${String(boundedMinute % 60).padStart(2, "0")}`;
};

export const sampleRoutePoints = (points = [], maxPoints = 800) => {
  const limit = Math.max(2, Math.floor(Number(maxPoints) || 800));
  if (points.length <= limit) return points;
  const sampled = [points[0]];
  const interiorLimit = limit - 2;
  const step = (points.length - 2) / interiorLimit;
  for (let index = 0; index < interiorLimit; index += 1) {
    sampled.push(points[1 + Math.floor(index * step)]);
  }
  sampled.push(points[points.length - 1]);
  return sampled;
};

export const prefersLightweightMap = (browser = {}) => {
  const userAgent = String(browser.userAgent || "");
  const query = String(browser.search || "");
  if (/(?:^|[?&])mapMode=full(?:&|$)/.test(query)) return false;
  if (/(?:^|[?&])mapMode=lite(?:&|$)/.test(query)) return true;
  if (/Tesla\//i.test(userAgent)) return true;
  const memory = Number(browser.deviceMemory);
  const cores = Number(browser.hardwareConcurrency);
  return (memory > 0 && memory <= 4) || (cores > 0 && cores <= 4);
};

const distanceInMeters = (left, right) => {
  const toRadians = value => (value * Math.PI) / 180;
  const latitudeDelta = toRadians(right.latitude - left.latitude);
  const longitudeDelta = toRadians(right.longitude - left.longitude);
  const leftLatitude = toRadians(left.latitude);
  const rightLatitude = toRadians(right.latitude);
  const haversine = (
    Math.sin(latitudeDelta / 2) ** 2
    + Math.cos(leftLatitude) * Math.cos(rightLatitude)
      * Math.sin(longitudeDelta / 2) ** 2
  );
  return 6371000 * 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
};

const pointTimestamp = point => {
  const timestamp = new Date(point.occurredAt).getTime();
  return Number.isFinite(timestamp) ? timestamp : Number(point.minute || 0) * 60000;
};

export const detectRouteStops = (points = [], options = {}) => {
  const radiusMeters = Number(options.radiusMeters ?? 35);
  const minPointCount = Number(options.minPointCount ?? 3);
  const minDurationMs = Number(options.minDurationMinutes ?? 10) * 60000;
  const stops = [];
  let cluster = [];

  const finishCluster = () => {
    if (cluster.length < minPointCount) {
      cluster = [];
      return;
    }

    const startedAt = pointTimestamp(cluster[0]);
    const endedAt = pointTimestamp(cluster[cluster.length - 1]);
    if (endedAt - startedAt < minDurationMs) {
      cluster = [];
      return;
    }

    stops.push({
      latitude: cluster.reduce((sum, point) => sum + point.latitude, 0) / cluster.length,
      longitude: cluster.reduce((sum, point) => sum + point.longitude, 0) / cluster.length,
      startedAt: cluster[0].occurredAt,
      endedAt: cluster[cluster.length - 1].occurredAt,
      startMinute: cluster[0].minute,
      endMinute: cluster[cluster.length - 1].minute,
      durationMinutes: Math.max(1, Math.round((endedAt - startedAt) / 60000)),
      pointCount: cluster.length,
    });
    cluster = [];
  };

  points.forEach((point) => {
    if (!Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)) return;
    if (!cluster.length) {
      cluster = [point];
      return;
    }

    const center = {
      latitude: cluster.reduce((sum, candidate) => sum + candidate.latitude, 0) / cluster.length,
      longitude: cluster.reduce((sum, candidate) => sum + candidate.longitude, 0) / cluster.length,
    };
    if (distanceInMeters(center, point) <= radiusMeters) {
      cluster.push(point);
      return;
    }

    finishCluster();
    cluster = [point];
  });
  finishCluster();

  return stops;
};

export const groupTicketActivityBySite = (events = [], options = {}) => {
  const currentMinute = Number(options.currentMinute ?? 1439);
  const radiusMeters = Number(options.radiusMeters ?? 75);
  const sites = [];

  events
    .filter(event => (
      (event.type === "ticket_created" || event.type === "ticket_printed")
      && Number.isFinite(event.latitude)
      && Number.isFinite(event.longitude)
      && event.minute <= currentMinute
    ))
    .forEach(event => {
      let site = sites.find(candidate => distanceInMeters(candidate, event) <= radiusMeters);
      if (!site) {
        site = {
          id: `site-${sites.length + 1}`,
          latitude: event.latitude,
          longitude: event.longitude,
          createdCount: 0,
          printedCount: 0,
          eventCount: 0,
          latestCreatedAt: '',
          latestPrintedAt: '',
          agentIds: [],
          agentNames: [],
        };
        sites.push(site);
      }

      site.eventCount += 1;
      site.latitude += (event.latitude - site.latitude) / site.eventCount;
      site.longitude += (event.longitude - site.longitude) / site.eventCount;
      if (event.type === "ticket_created") site.createdCount += 1;
      if (event.type === "ticket_printed") site.printedCount += 1;
      if (
        event.type === "ticket_created"
        && (!site.latestCreatedAt || Date.parse(event.occurredAt) > Date.parse(site.latestCreatedAt))
      ) {
        site.latestCreatedAt = event.occurredAt;
      }
      if (
        event.type === "ticket_printed"
        && (!site.latestPrintedAt || Date.parse(event.occurredAt) > Date.parse(site.latestPrintedAt))
      ) {
        site.latestPrintedAt = event.occurredAt;
      }
      if (event.agentId && !site.agentIds.includes(event.agentId)) {
        site.agentIds.push(event.agentId);
      }
      if (event.agentName && !site.agentNames.includes(event.agentName)) {
        site.agentNames.push(event.agentName);
      }
    });

  return sites;
};

export const getVisibleReplayData = (dataset, options = {}) => {
  const currentMinute = Number(options.currentMinute ?? 1439);
  const role = options.role || "all";
  const agentId = options.agentId || "all";
  const status = options.status || "all";
  const zone = options.zone || "all";
  const agentsWithPositions = new Set(
    dataset.positions.map((position) => position.agentId)
  );
  const agents = dataset.agents.filter((agent) => (
    agentsWithPositions.has(agent.id) &&
    (role === "all" || agent.role === role) &&
    (agentId === "all" || agent.id === agentId) &&
    (status === "all" || agent.status === status) &&
    (zone === "all" || agent.zone === zone)
  ));
  const agentIds = new Set(agents.map((agent) => agent.id));

  return {
    agents,
    positions: dataset.positions.filter((point) => (
      agentIds.has(point.agentId) && point.minute <= currentMinute
    )),
    events: dataset.events.filter((event) => (
      agentIds.has(event.agentId) && event.minute <= currentMinute
    )),
    ticketActivityEvents: (dataset.ticketActivityEvents || []).filter((event) => (
      agentIds.has(event.agentId) && event.minute <= currentMinute
    )),
  };
};
